import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { delay, Observable, of } from 'rxjs';
import { getQueryParams } from '@hglobal/common';
import { environment } from '@fb/environments';

import {
  GetMessagesReq,
  GetMessagesRes,
  SendQuestionRes,
  SendQuestionReq,
  LeaveFeedbackReq,
  GetUserChatsRes,
  ToggleReactionReq,
} from '../../models';
import { getMessagesMock, getUserChatsMock, sendMessageMock } from './mocks';

@Injectable()
export class AiChatFormService {
  #httpClient = inject(HttpClient);
  #baseUrl = `${environment.apiHost}ai-chatbot/`;

  getUserChats(): Observable<GetUserChatsRes> {
    if (environment.useMocks) {
      return of(getUserChatsMock()).pipe(delay(500));
    }

    return this.#httpClient.get<GetUserChatsRes>(`${this.#baseUrl}chats/`);
  }

  getMessages(query: GetMessagesReq): Observable<GetMessagesRes> {
    if (environment.useMocks) {
      return of(getMessagesMock(query)).pipe(delay(500));
    }

    return this.#httpClient.get<GetMessagesRes>(
      `${this.#baseUrl}chats/${query.chatId}/messages/`,
      { params: getQueryParams(query, ['limit', 'offset']) },
    );
  }

  sendQuestion(question: SendQuestionReq): Observable<SendQuestionRes> {
    if (environment.useMocks) {
      return of(sendMessageMock(question)).pipe(delay(1200));
    }

    return this.#httpClient.post<SendQuestionRes>(
      `${this.#baseUrl}message/`,
      question,
    );
  }

  toggleReaction(toggleReaction: ToggleReactionReq): Observable<void> {
    if (environment.useMocks) {
      return of().pipe(delay(500));
    }

    return this.#httpClient.patch<void>(
      `${this.#baseUrl}message/${toggleReaction.id}/`,
      { reactionType: toggleReaction.reaction },
    );
  }

  leaveFeedback(feedback: LeaveFeedbackReq): Observable<void> {
    if (environment.useMocks) {
      return of().pipe(delay(500));
    }

    return this.#httpClient.post<void>(
      `${this.#baseUrl}message/${feedback.id}/feedback/`,
      {
        text: feedback.text,
        feedbackType: feedback.feedbackType,
      },
    );
  }

  dryRequest(): Observable<void> {
    return this.#httpClient.get<void>(`${this.#baseUrl}dry/`);
  }
}
